import { 
  ScanSearch, 
  KeyRound, 
  Zap, 
  Activity, 
  FlaskConical, 
  FileText,
  Check
} from "lucide-react";

const features = [
  {
    icon: ScanSearch,
    title: "Code Scanner Engine",
    description: "Analyzes agent source code (Python/JavaScript) to extract intent and required permissions using SLM (CodeBERT). Determines exactly what each agent is designed to do.",
    capabilities: [
      "Intent Profile Generation",
      "Permission Mapping",
      "85%+ Accuracy Target"
    ],
    gradient: "from-violet-500 to-purple-600",
    iconBg: "bg-violet-500/10",
    iconColor: "text-violet-400"
  },
  {
    icon: KeyRound,
    title: "IAM Integration Layer",
    description: "Connects to Okta or Azure AD to read and update permissions. Automatically removes excessive privileges, reducing agent privilege scope by 40-60%.",
    capabilities: [
      "Okta & Azure AD Support",
      "One-Click Permission Removal",
      "Surgical Precision Updates"
    ],
    gradient: "from-indigo-500 to-blue-600",
    iconBg: "bg-indigo-500/10",
    iconColor: "text-indigo-400"
  },
  {
    icon: Zap,
    title: "Hybrid JIT Token Service",
    description: "Issues temporary credentials (30-120 sec TTL) for high-risk operations only (DELETE/EXPORT/ADMIN). Integrates with Jira for business trigger verification.",
    capabilities: [
      "<200ms P95 Latency",
      "Jira Integration",
      "Auto-Revocation"
    ],
    gradient: "from-amber-500 to-orange-600",
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-400"
  },
  {
    icon: Activity,
    title: "Behavioral Monitoring",
    description: "Real-time agent behavior analysis via Kafka data pipeline. 30-day baseline establishment with 3-sigma deviation alerts and rule-based triggers.",
    capabilities: [
      "Real-Time Anomaly Detection",
      "<5 Min Alert Latency",
      "PagerDuty/Slack Integration"
    ],
    gradient: "from-emerald-500 to-teal-600",
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-400"
  },
  {
    icon: FlaskConical,
    title: "AI Governance Sandbox",
    description: "Isolated environment (dedicated VPCs, K8s namespaces) for testing prompts and autonomous agents before production deployment. Synthetic data only.",
    capabilities: [
      "Prompt Evaluation",
      "Pre-Production Testing",
      "Sandbox-to-Prod Promotion"
    ],
    gradient: "from-cyan-500 to-blue-600",
    iconBg: "bg-cyan-500/10",
    iconColor: "text-cyan-400"
  },
  {
    icon: FileText,
    title: "Audit & Compliance",
    description: "Immutable, append-only log storage for compliance and forensics. 12-month retention with cryptographic hashing for tamper detection. EU AI Act Article 72 ready.",
    capabilities: [
      "Tamper-Proof Logs",
      "Compliance Reports",
      "CSV Export for Auditors"
    ],
    gradient: "from-rose-500 to-pink-600",
    iconBg: "bg-rose-500/10",
    iconColor: "text-rose-400"
  }
];

export const ProductFeatures = () => {
  return (
    <section 
      id="features" 
      className="py-20 sm:py-28 lg:py-36 px-6 lg:px-10 bg-slate-950/50 relative overflow-hidden"
      data-testid="features-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-violet-500" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12 lg:mb-20 reveal">
          <span 
            className="text-[9px] sm:text-[10px] font-extrabold text-violet-500 uppercase tracking-[0.4em] sm:tracking-[0.6em] mb-4 block"
            data-testid="features-label"
          >
            Platform Capabilities
          </span>
          <h2 
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tighter mb-6"
            data-testid="features-headline"
          >
            Enterprise-Grade<br />
            <span className="text-violet-500">AI Agent Governance</span>
          </h2>
          <p className="text-base lg:text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
            A complete platform for managing AI agent entitlements at scale. From code analysis to real-time monitoring, everything you need for EU AI Act compliance.
          </p>
        </div>

        {/* Features Grid */}
        <div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="features-grid"
        >
          {features.map((feature, index) => (
            <div 
              key={index}
              className="group relative p-6 lg:p-8 rounded-2xl bg-slate-900/40 border border-slate-800 hover:border-violet-500/30 transition-all duration-500 hover:bg-slate-900/60"
              data-testid={`feature-card-${index}`}
            >
              {/* Hover Glow */}
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_50%_0%,rgba(139,92,246,0.08),transparent_70%)]" />
              
              {/* Icon */}
              <div className={`w-14 h-14 rounded-xl ${feature.iconBg} border border-slate-700/50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className={`w-7 h-7 ${feature.iconColor}`} />
              </div>

              {/* Content */}
              <h3 className="text-lg lg:text-xl font-bold text-white mb-3">{feature.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-6">
                {feature.description}
              </p>

              {/* Capabilities */}
              <ul className="space-y-2">
                {feature.capabilities.map((cap, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-slate-500">
                    <Check className={`w-3.5 h-3.5 ${feature.iconColor}`} />
                    <span className="font-medium uppercase tracking-wide">{cap}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
